import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import {
    DataTable,
    MetricMatrix,
    PanelFrame,
    SectionFrame,
    StatusBadge,
    ToneBanner,
    eyebrowSx,
    formatChipText,
    formatNumber,
    toneForStatus,
    useDensity,
} from '../components/SharedFrames';
import { usePlatformStateModel } from '../usePlatformStateModel';

export default function PlatformStateSection() {
    const { compact } = useDensity();
    const {
        infra,
        infraLoading,
        mdNorm,
        quantPlatform,
        chainRows,
        infraIssues,
        serviceCount,
        operationalCount,
        platformStatusTone,
        brokerLoginDigest,
        brokerLoginError,
        brokerLoginStatusLabel,
        platformOverviewMetrics,
        mdNormMetrics,
        quantMetrics,
        brokerLoginMetrics,
        platformBanner,
    } = usePlatformStateModel();

    return (
        <SectionFrame
            accent="platform"
            eyebrow="Platform State"
            title="Platform Operations Rail"
            description="Market-data ingest health, quant-platform runtime integrity, option-chain coverage, and daily broker-login telemetry are isolated into a dedicated infrastructure lane."
            status={
                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                    <StatusBadge
                        label={`Infra ${formatChipText(infra?.overallStatus || (infraLoading ? 'loading' : 'waiting'))}`}
                        tone={platformStatusTone}
                    />
                    <StatusBadge
                        label={serviceCount ? `${operationalCount}/${serviceCount} services` : 'No services'}
                        tone={serviceCount && operationalCount === serviceCount ? 'success' : 'warning'}
                    />
                </Stack>
            }
        >
            <ToneBanner {...platformBanner} />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' },
                    gap: compact ? 1.5 : 2,
                }}
            >
                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 4' } }}>
                    <PanelFrame
                        accent="platform"
                        eyebrow="Overview"
                        title="Platform Summary"
                        description="Roll-up of service health, coverage, and data age across the infra stack."
                    >
                        <MetricMatrix
                            metrics={platformOverviewMetrics}
                            columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }}
                        />
                        <Divider />
                        <Typography sx={eyebrowSx}>Exceptions</Typography>
                        {infraIssues.length ? (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {infraIssues.map((service) => (
                                    <StatusBadge
                                        key={service.key}
                                        label={`${service.key.split(':')[1]} ${formatChipText(service.status)}`}
                                        tone={toneForStatus(service.status)}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No active infra exceptions are reported right now.
                            </Typography>
                        )}
                    </PanelFrame>
                </Box>

                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 4' } }}>
                    <PanelFrame
                        accent="platform"
                        eyebrow="Market Data"
                        title="md_norm"
                        description="WebSocket ingest, Redis publish latency, reconnects, and active universe visibility."
                        action={<StatusBadge status={mdNorm?.status} />}
                    >
                        <MetricMatrix
                            metrics={mdNormMetrics}
                            columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }}
                        />
                    </PanelFrame>
                </Box>

                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 4' } }}>
                    <PanelFrame
                        accent="platform"
                        eyebrow="Runtime"
                        title="quant-platform"
                        description="Redis stream consumption, option-chain coverage, kill-switch posture, and snapshot freshness."
                        action={<StatusBadge status={quantPlatform?.status} />}
                    >
                        <MetricMatrix
                            metrics={quantMetrics}
                            columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }}
                        />
                    </PanelFrame>
                </Box>

                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 4' } }}>
                    <PanelFrame
                        accent="platform"
                        eyebrow="Broker"
                        title="broker_login"
                        description="Daily Zerodha login service. Systemd timer fires at 07:55 IST and writes access tokens to Redis."
                        action={<StatusBadge status={brokerLoginDigest?.overallStatus || brokerLoginStatusLabel} />}
                    >
                        <MetricMatrix
                            metrics={brokerLoginMetrics}
                            columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }}
                        />
                        {brokerLoginError ? (
                            <Typography variant="caption" color="error">
                                Could not load broker_login telemetry: {brokerLoginError.message || 'unknown error'}
                            </Typography>
                        ) : null}
                    </PanelFrame>
                </Box>
            </Box>

            <DataTable
                title="Chain Coverage"
                description="Current option-chain subscription coverage emitted by quant-platform."
                rows={chainRows}
                emptyMessage="No active option-chain telemetry rows are available yet."
                accent="platform"
                columns={[
                    { key: 'underlying', label: 'Underlying' },
                    { key: 'expiry', label: 'Expiry' },
                    { key: 'status', label: 'Status', render: (value) => <StatusBadge status={value} /> },
                    { key: 'atmStrike', label: 'ATM', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                    { key: 'strikeCount', label: 'Strikes', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                    { key: 'subscribedTokenCount', label: 'Tokens', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                    { key: 'futureExpiry', label: 'Future Expiry' },
                    { key: 'snapshotAgeS', label: 'Snapshot Age', render: (value) => value == null ? '—' : `${formatNumber(value, 1)}s`, align: 'right', monospace: true },
                ]}
            />
        </SectionFrame>
    );
}
